import { useMemo } from 'react';
import { Row, Col, Empty, Skeleton } from 'antd';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { StatCard } from './StatCard';
import { queryDataSource } from '../api/query';
import type { DashboardWidget, ReportConfig } from '../types/panelConfig';

const accentMap: Record<string, string> = {
  blue: 'var(--color-primary)',
  green: 'var(--color-positive)',
  purple: 'var(--color-tier-ileri)',
  orange: 'var(--color-warning)',
  red: 'var(--color-negative)',
  cyan: '#0e7490',
};

const donutPalette = ['#0f172a', '#475569', '#b45309', '#6b21a8', '#16a34a', '#0e7490'];
const currencyFmt = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 });
const compactFmt = new Intl.NumberFormat('tr-TR', { notation: 'compact', maximumFractionDigits: 1 });

interface DynamicDashboardProps {
  widgets: DashboardWidget[];
  reports: ReportConfig[];
}

export function DynamicDashboard({ widgets, reports }: DynamicDashboardProps) {
  const widgetQueries = widgets.map((widget) => {
    const report = reports.find((r) => r.code === widget.reportCode);
    return { widget, report, sourceId: report?.sourceId };
  });

  const queries = useQueries({
    queries: widgetQueries.map((wq) => ({
      queryKey: ['dashboard-widget', wq.widget.reportCode, wq.sourceId],
      queryFn: async () => {
        if (!wq.sourceId) return null;
        const res = await queryDataSource({ sourceId: wq.sourceId });
        return res.data || [];
      },
      enabled: !!wq.sourceId,
      staleTime: 1000 * 60 * 5,
    })),
  });

  const ciroReport = reports.find((r) => r.code === 'gunluk-ciro');
  const karlilikReport = reports.find((r) => r.code === 'satis-karlilik');

  const ciroQuery = useQuery({
    queryKey: ['dashboard-chart-ciro', ciroReport?.sourceId],
    queryFn: async () => {
      if (!ciroReport?.sourceId) return [];
      const res = await queryDataSource({ sourceId: ciroReport.sourceId });
      return (res.data as Record<string, unknown>[]) || [];
    },
    enabled: !!ciroReport?.sourceId,
    staleTime: 1000 * 60 * 5,
  });

  const karlilikQuery = useQuery({
    queryKey: ['dashboard-chart-karlilik', karlilikReport?.sourceId],
    queryFn: async () => {
      if (!karlilikReport?.sourceId) return [];
      const res = await queryDataSource({ sourceId: karlilikReport.sourceId });
      return (res.data as Record<string, unknown>[]) || [];
    },
    enabled: !!karlilikReport?.sourceId,
    staleTime: 1000 * 60 * 5,
  });

  const ciroSeries = useMemo(() => {
    const rows = ciroQuery.data ?? [];
    if (rows.length === 0) return [];
    const byDate = new Map<string, number>();
    for (const r of rows) {
      const tarih = String(r['TARIH'] ?? '');
      if (!tarih) continue;
      const val = Number(r['TOPLAM'] ?? 0);
      byDate.set(tarih, (byDate.get(tarih) ?? 0) + val);
    }
    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-30)
      .map(([tarih, toplam]) => ({
        tarih: tarih.slice(5),
        Ciro: Math.round(toplam),
      }));
  }, [ciroQuery.data]);

  const karlilikTop = useMemo(() => {
    const rows = karlilikQuery.data ?? [];
    if (rows.length === 0) return [];
    const byName = new Map<string, number>();
    for (const r of rows) {
      const ad = String(r['STOK_ADI'] ?? '');
      if (!ad) continue;
      const val = Number(r['KAR'] ?? 0);
      byName.set(ad, (byName.get(ad) ?? 0) + val);
    }
    return Array.from(byName.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name, value: Math.round(value) }));
  }, [karlilikQuery.data]);

  if (widgets.length === 0 && !ciroReport && !karlilikReport) {
    return <Empty description="Dashboard henüz yapılandırılmamış" />;
  }

  return (
    <div>
      <div style={{ margin: '4px 0 16px' }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--color-text-primary)', letterSpacing: '-0.01em' }}>
          Genel Bakış
        </h1>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
          Son 30 gün · canlı demo verisi
        </div>
      </div>

      {widgets.length > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {widgetQueries.map((wq, index) => {
            const query = queries[index];
            const { statField, label, aggregation, color } = wq.widget.config;

            let value: string = '—';
            if (query.data && Array.isArray(query.data) && query.data.length > 0) {
              const values = query.data.map((row: Record<string, unknown>) => Number(row[statField]) || 0);
              let agg = 0;
              switch (aggregation) {
                case 'sum': agg = values.reduce((a: number, b: number) => a + b, 0); break;
                case 'avg': agg = values.reduce((a: number, b: number) => a + b, 0) / values.length; break;
                case 'count': agg = values.length; break;
                default: agg = values.reduce((a: number, b: number) => a + b, 0);
              }
              value = currencyFmt.format(agg);
            }

            return (
              <Col xs={24} sm={12} lg={6} key={wq.widget.reportCode}>
                <StatCard
                  title={label}
                  value={value}
                  accentColor={accentMap[color || 'blue'] ?? accentMap.blue}
                  loading={query.isLoading}
                />
              </Col>
            );
          })}
        </Row>
      )}

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={15}>
          <Panel title="Günlük ciro" subtitle="Son 30 gün, toplam ciro">
            {ciroQuery.isLoading ? (
              <Skeleton.Node active style={{ width: '100%', height: 260 }} />
            ) : ciroSeries.length === 0 ? (
              <Empty description="Veri yok" style={{ padding: 40 }} />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={ciroSeries} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="var(--color-border)" vertical={false} />
                  <XAxis
                    dataKey="tarih"
                    tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                    axisLine={{ stroke: 'var(--color-border)' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => compactFmt.format(v as number)}
                    width={48}
                  />
                  <ChartTooltip
                    formatter={(v) => currencyFmt.format(Number(v))}
                    contentStyle={{
                      borderRadius: 4,
                      border: '1px solid var(--color-border)',
                      fontSize: 12,
                      padding: '6px 10px',
                      boxShadow: '0 4px 16px var(--shadow-color-strong)',
                    }}
                    labelStyle={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Ciro"
                    stroke="var(--color-primary)"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={{ r: 4, fill: 'var(--color-primary)' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Col>

        <Col xs={24} lg={9}>
          <Panel title="En karlı 5 ürün" subtitle="Toplam kar">
            {karlilikQuery.isLoading ? (
              <Skeleton.Node active style={{ width: '100%', height: 260 }} />
            ) : karlilikTop.length === 0 ? (
              <Empty description="Veri yok" style={{ padding: 40 }} />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={karlilikTop} dataKey="value" nameKey="name" innerRadius={54} outerRadius={82} paddingAngle={1}>
                    {karlilikTop.map((_, i) => (
                      <Cell key={i} fill={donutPalette[i % donutPalette.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip
                    formatter={(v) => currencyFmt.format(Number(v))}
                    contentStyle={{ borderRadius: 4, border: '1px solid var(--color-border)', fontSize: 12 }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Col>
      </Row>
    </div>
  );
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--color-card-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{subtitle}</div>}
      </div>
      <div style={{ padding: 10 }}>{children}</div>
    </div>
  );
}
